/**
 * Client block/allow script settings — api instance + shared thunk error payload.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "utils/api";
import { getSessionData } from "utils/common";
import { axiosRejectPayload } from "utils/httpErrors";

const initialState = {
  totalRecordScriptSettings      : 0,
  scriptSettingList              : [],
  ScriptLoading                  : false,
  startApiCallScriptSettingList  : false,
  isScriptSettingUpdated         : false,
  searchTextScriptSettings       : "",
  accountSettingType             : 1,
  selectedAccountInScriptSetting : {},
  currentPageScriptSettings      : 1,
  pageSizeScriptSettings         : 10,
  isScriptSettingGet             : false,
  segmentId                      : {},
};

// ── GET blocked scripts per user
// GET settings/client-block-allow-script/get-block-allow-symbol-by-user/{userId}/{pageNo}/{pageSize}
export const getScriptSettingList = createAsyncThunk(
  "/setting/script/get",
  async (data, { rejectWithValue }) => {
    try {
      const { searchText, pageNo, pageSize, appendData, userId } = data;

      const url = `settings/client-block-allow-script/get-block-allow-symbol-by-user/${userId}/${pageNo}/${pageSize}${
        searchText ? `?searchText=${searchText}` : ""
      }`;

      const { data: res } = await api.get(url);

      if (res.status === 200) {
        return { data: res.data, appendData, searchText };
      }

      return rejectWithValue(res);

    } catch (error) {
      console.error("getScriptSettingList error:", error?.response?.status, error?.response?.data);
      return rejectWithValue(axiosRejectPayload(error));
    }
  }
);

// ── ADD / UPDATE blocked symbol
// POST settings/client-block-allow-script/save-block-allow-symbol
export const addSymbolSetting = createAsyncThunk(
  "/setting/block-scripts/add",
  async (paramData, { rejectWithValue }) => {
    try {
      const { userId: createdBy } = getSessionData();

      const payload = {
        id        : paramData.id,
        userId    : Number(paramData.userId.value),
        createdBy : createdBy,
        symbol    : paramData.symbol.value,
        segmentId : paramData.segmentId.value,
        isBlocked : paramData.settingType == 1,
      };

      const { data } = await api.post(
        "settings/client-block-allow-script/save-block-allow-symbol",
        payload
      );

      if (data.status === 200) return data;
      return rejectWithValue(data);

    } catch (error) {
      return rejectWithValue(axiosRejectPayload(error));
    }
  }
);

// ── DELETE blocked symbol
// POST settings/client-block-allow-script/delete-block-allow-symbol
export const deleteScriptSetting = createAsyncThunk(
  "/setting/block-scripts/delete",
  async (data, { rejectWithValue }) => {
    try {
      const { data: res } = await api.post(
        "settings/client-block-allow-script/delete-block-allow-symbol",
        data
      );

      if (res.status === 200) return res;
      return rejectWithValue(res);

    } catch (error) {
      return rejectWithValue(axiosRejectPayload(error));
    }
  }
);

export const settingScriptSlice = createSlice({
  name: "settingScript",
  initialState,
  reducers: {
    setIsScriptSettingUpdated         : (state, action) => { state.isScriptSettingUpdated          = action.payload; },
    setStartApiCallScriptSettingList  : (state, action) => { state.startApiCallScriptSettingList   = action.payload; },
    setSearchTextScriptSetting        : (state, action) => { state.searchTextScriptSettings        = action.payload; },
    setStartPageScriptSetting         : (state, action) => { state.currentPageScriptSettings       = action.payload; },
    setScriptSettingList              : (state, action) => { state.scriptSettingList               = action.payload; },
    setAccountSettingType             : (state, action) => { state.accountSettingType              = action.payload; },
    setSelectedAccountInScriptSetting : (state, action) => { state.selectedAccountInScriptSetting = action.payload; },
    setSegmentId                      : (state, action) => { state.segmentId                      = action.payload; },
    setIsScriptSettingGet             : (state, action) => { state.isScriptSettingGet              = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      // getScriptSettingList
      .addCase(getScriptSettingList.pending, (state) => {
        state.ScriptLoading = true;
      })
      .addCase(getScriptSettingList.rejected, (state) => {
        state.scriptSettingList          = [];
        state.ScriptLoading              = false;
        state.totalRecordScriptSettings  = 0;
      })
      .addCase(getScriptSettingList.fulfilled, (state, action) => {
        const records = action.payload.data?.records || [];
        state.scriptSettingList = action.payload.appendData
          ? [...state.scriptSettingList, ...records]
          : records;
        state.ScriptLoading             = false;
        state.totalRecordScriptSettings = action.payload.searchText
          ? records.length
          : (action.payload.data?.totalRecords || 0);
      })

      // addSymbolSetting
      .addCase(addSymbolSetting.pending,   (state) => { state.isScriptSettingUpdated = false; })
      .addCase(addSymbolSetting.rejected,  (state) => { state.isScriptSettingUpdated = false; })
      .addCase(addSymbolSetting.fulfilled, (state) => { state.isScriptSettingUpdated = true;  })

      // deleteScriptSetting
      .addCase(deleteScriptSetting.pending,   (state) => { state.isScriptSettingUpdated = false; })
      .addCase(deleteScriptSetting.rejected,  (state) => { state.isScriptSettingUpdated = false; })
      .addCase(deleteScriptSetting.fulfilled, (state) => { state.isScriptSettingUpdated = true;  });
  },
});

export const {
  setIsScriptSettingUpdated,
  setStartPageScriptSetting,
  setScriptSettingList,
  setSearchTextScriptSetting,
  setAccountSettingType,
  setSelectedAccountInScriptSetting,
  setSegmentId,
  setIsScriptSettingGet,
} = settingScriptSlice.actions;

export default settingScriptSlice.reducer;